import type { ReactNode } from "react";
import { categories } from "@/mocks/categories";
import { tags } from "@/mocks/tags";
import {
  DATE_OPTIONS,
  DURATION_OPTIONS,
  REGION_OPTIONS,
  type VideoFilterState,
} from "@/utils/videoFilters";

interface FilterPanelProps {
  filter: VideoFilterState;
  onChange: (patch: Partial<VideoFilterState>) => void;
  onReset: () => void;
  showCategory: boolean;
  showTag: boolean;
  activeCount: number;
}

interface ChipGroupProps {
  title: string;
  children: ReactNode;
}

function ChipGroup({ title, children }: ChipGroupProps) {
  return (
    <div className="border-b border-background-200 pb-4">
      <h4 className="mb-3 font-heading text-sm font-semibold text-foreground-900">{title}</h4>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

interface ChipProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

function Chip({ active, label, onClick }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors duration-200 ${
        active
          ? "bg-primary-500 text-background-50"
          : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600"
      }`}
    >
      {label}
    </button>
  );
}

export default function FilterPanel({
  filter,
  onChange,
  onReset,
  showCategory,
  showTag,
  activeCount,
}: FilterPanelProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-base font-bold text-foreground-950">筛选条件</h3>
        <button
          type="button"
          onClick={onReset}
          disabled={activeCount === 0}
          className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors duration-200 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="ri-refresh-line"></i>
          重置
        </button>
      </div>

      {showCategory ? (
        <ChipGroup title="视频分类">
          <Chip active={filter.category === "all"} label="全部" onClick={() => onChange({ category: "all" })} />
          {categories.map((cat) => (
            <Chip
              key={cat.slug}
              active={filter.category === cat.slug}
              label={cat.name}
              onClick={() => onChange({ category: cat.slug })}
            />
          ))}
        </ChipGroup>
      ) : null}

      {showTag ? (
        <ChipGroup title="视频标签">
          <Chip active={filter.tag === "all"} label="全部" onClick={() => onChange({ tag: "all" })} />
          {tags.map((tag) => (
            <Chip
              key={tag.slug}
              active={filter.tag === tag.slug}
              label={tag.name}
              onClick={() => onChange({ tag: tag.slug })}
            />
          ))}
        </ChipGroup>
      ) : null}

      <ChipGroup title="地区">
        {REGION_OPTIONS.map((region, index) => (
          <Chip
            key={region}
            active={filter.region === (index === 0 ? "all" : region)}
            label={region}
            onClick={() => onChange({ region: index === 0 ? "all" : region })}
          />
        ))}
      </ChipGroup>

      <ChipGroup title="上传时间">
        {DATE_OPTIONS.map((option) => (
          <Chip
            key={option.value}
            active={filter.date === option.value}
            label={option.label}
            onClick={() => onChange({ date: option.value })}
          />
        ))}
      </ChipGroup>

      <ChipGroup title="视频时长">
        {DURATION_OPTIONS.map((option) => (
          <Chip
            key={option.value}
            active={filter.duration === option.value}
            label={option.label}
            onClick={() => onChange({ duration: option.value })}
          />
        ))}
      </ChipGroup>
    </div>
  );
}